import { useEffect, useRef } from "preact/hooks";

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "textarea:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(", ");

// ------------------------------------------------------------------------
// A single, shared `document` keydown listener + an explicit stack of
// active modals (rather than one `addEventListener` per open dialog).
// Hayloft genuinely nests overlays (e.g. HandOutWorkFlow -> Assignee-
// PickerSheet, PersonDetailOverlay -> LinkCardOverlay -> ConfirmDialog,
// ChoreEditorOverlay -> AssigneePickerSheet/ConfirmDialog) — with a naive
// per-component `document.addEventListener` approach, listener execution
// order follows REGISTRATION order (the outer/earlier-mounted modal),
// which is backwards: Escape/Tab must always go to the TOPMOST (most
// recently opened) modal only, never the one underneath it. A shared
// stack, dispatching only to its top entry, gets this right regardless of
// mount/unmount order.
//
// Sprint-011 C1 (TD-012, SA plan §3) — the SAME stack now also backs a
// shared `popstate` listener: one ephemeral `history.pushState({
// hayloftOverlay: true })` per open modal, and hardware/browser Back peels
// the stack one layer at a time — byte-identical order to the Escape
// semantics above, "for free," because it's the same stack. See the
// `popstate` block below for the full mechanism.
// ------------------------------------------------------------------------
interface StackEntry {
  onKeyDown: (e: KeyboardEvent) => void;
  /** The caller's real close callback. TD-012 clause 3: the ONLY code
   * that ever calls this is the shared `popstate` handler below — Escape,
   * the ×/Cancel button, and backdrop clicks all route through
   * `history.back()` instead (see `requestClose` in `useModalA11y`), so a
   * UI dismiss and a hardware/browser Back converge on this one call
   * site. No double-close, no phantom history entry. */
  onClose: () => void;
  /** Sprint-017 E2 (TD-039) — an OPTIONAL dirty-state guard. `undefined`
   * for every overlay that hasn't opted in (the default), in which case
   * the shared `popstate` handler takes the byte-identical one-shot close
   * path it always has. When a consumer opts in (a form with unsaved
   * edits), the `popstate` handler instead AWAITS this guard before
   * closing: a `false` result ABORTS the close — `onClose` is not called
   * and the ephemeral history entry the Back already consumed is re-pushed
   * so real history depth stays consistent with the still-open overlay; a
   * `true` result closes exactly as the default path does. This is the one
   * thing the pre-E2 one-shot `onClose` could not do (see
   * `PersonDetailOverlay`'s former dirty-name Back-button-only guard). */
  guard?: () => boolean | Promise<boolean>;
  /** True once this entry's ephemeral history entry is being (or has
   * been) consumed — guards re-entrant/duplicate `onClose` calls when a
   * UI dismiss and a hardware Back race, and lets the cleanup-time
   * reconciliation below (`reconcileUnclosedEntry`) tell "already
   * consumed via popstate" apart from "closed some other way, still owes
   * the browser a `history.back()`." */
  closing: boolean;
}

const modalStack: StackEntry[] = [];
let globalListenerInstalled = false;

// Sprint-011 C1 — when OUR OWN code drives a `history.back()` call to
// consume an entry that closed WITHOUT going through `requestClose()`
// first (see `reconcileUnclosedEntry`), we already know which entry it
// targets and have already run its close side effects through the normal
// component-unmount path, so the `popstate` this produces must not be
// dispatched a second time (it would otherwise mis-fire onto whatever
// entry is now on top of the stack — e.g. a parent overlay one level
// down in a nested stack).
let suppressNextPopstate = false;

function ensureGlobalListener(): void {
  if (globalListenerInstalled) return;
  globalListenerInstalled = true;
  document.addEventListener(
    "keydown",
    (e) => {
      const top = modalStack[modalStack.length - 1];
      top?.onKeyDown(e);
    },
    true,
  );

  // TD-012 (SA plan §3 clauses 1 & 5) — one shared `popstate` listener,
  // dispatching only to the TOPMOST open modal, exactly mirroring the
  // keydown stack above. If `modalStack` is empty, a route change has no
  // open overlay to intercept — leave it untouched for `wouter`'s own
  // `pushState`-driven navigation (clause 5's coexistence rule). Guarding
  // on a non-empty stack before acting also satisfies clause 4's "never
  // `history.back()` past the app's own initial entry": we only ever act
  // on `popstate` events that correspond to one of our own ephemeral
  // pushes.
  window.addEventListener("popstate", () => {
    if (suppressNextPopstate) {
      suppressNextPopstate = false;
      return;
    }
    const top = modalStack[modalStack.length - 1];
    if (!top || top.closing) return;

    // Default (un-guarded) path — byte-identical to the original one-shot
    // close: mark closing, call the real `onClose`, done, all synchronously.
    // Every existing overlay hits exactly this branch (none passes a
    // `guard`), so TD-012's modal-stack behavior is untouched for them.
    if (!top.guard) {
      top.closing = true;
      top.onClose();
      return;
    }

    // Sprint-017 E2 (TD-039) cancelable path — engaged ONLY for a consumer
    // that opted in with a `guard`. The hardware/browser Back (or our own
    // `close()` -> `history.back()`) that produced this `popstate` has
    // ALREADY consumed this modal's ephemeral entry; we now ask the guard
    // whether the close may proceed.
    top.closing = true;
    Promise.resolve(top.guard()).then((proceed) => {
      if (proceed) {
        // Approved (not dirty, or the user chose "Discard"): finish the
        // close through the SAME single `onClose` call site as the default
        // path — no second source of truth for close.
        top.onClose();
      } else {
        // Aborted (dirty + the discard prompt was declined, or is still
        // being shown): restore the entry the Back consumed so real history
        // depth stays consistent with the still-open overlay, and reset
        // `closing` so a subsequent Back can guard again. `history.pushState`
        // never emits `popstate`, so this is race-free and never re-enters
        // this handler.
        top.closing = false;
        history.pushState({ hayloftOverlay: true }, "");
      }
    });
  });
}

function pushModal(entry: StackEntry): void {
  ensureGlobalListener();
  modalStack.push(entry);
}

function removeModal(entry: StackEntry): void {
  const idx = modalStack.indexOf(entry);
  if (idx !== -1) modalStack.splice(idx, 1);
}

// Sprint-011 G1 fix (TD-012 regression) — entries whose ephemeral
// `pushState` hasn't been consumed yet, queued by `reconcileUnclosedEntry`
// below rather than acted on immediately. See that function's docstring
// for why: `history.back()`'s actual traversal (and the `popstate` it
// produces) is a task the browser queues for a LATER tick, never
// synchronous within the calling script, no matter how soon it's called.
const pendingReconciles: StackEntry[] = [];
let reconcileFlushScheduled = false;

function scheduleReconcileFlush(): void {
  if (reconcileFlushScheduled) return;
  reconcileFlushScheduled = true;
  queueMicrotask(() => {
    reconcileFlushScheduled = false;
    // Anything still queued here had no same-tick pairing open (see
    // `pushOrReplaceModal` below) — a genuine solo close, needing a real
    // (necessarily async) browser Back to shrink the stack back down,
    // exactly as before this fix.
    const leftoverCount = pendingReconciles.length;
    pendingReconciles.length = 0;
    for (let i = 0; i < leftoverCount; i++) {
      suppressNextPopstate = true;
      history.back();
    }
  });
}

/**
 * TD-012 (SA plan §3 clause 4) — reconcile an entry that is unmounting
 * (its `active` prop flipped to false, or the component unmounted
 * outright) WITHOUT having gone through `requestClose()`/the `popstate`
 * handler first — e.g. a form submits successfully and the caller closes
 * itself programmatically. Its ephemeral `pushState` entry is still
 * sitting in browser history unconsumed; left alone it becomes a
 * "phantom" entry the next real Back has to burn through before it does
 * anything visible.
 *
 * Sprint-011 G1 fix — this used to call `history.back()` immediately,
 * synchronously. That's exactly what broke: `history.back()`'s real
 * traversal is a browser-QUEUED task (per spec, confirmed against real
 * Chromium), never synchronous, no matter how early it's called. When a
 * SIBLING modal opens in the very same synchronous commit — e.g.
 * `QuickAdd.tsx`'s menu -> "Add a task" sheet (`startAction`:
 * `setMenuOpen(false)` + `setAction(...)` in one handler), or
 * `CalendarCreateSheet`'s own `active: open && !pickerOpen` toggle
 * around its nested `AssigneePickerSheet` — that sibling's `pushState`
 * (always synchronous) landed BEFORE this queued `back()` had actually
 * traversed anywhere, so the eventual traversal jumped past BOTH the
 * closing entry AND the newly-opened one, one level below the app's own
 * real route history — and the next `history.back()` (Escape on the new
 * sheet) then traveled one level further than intended: a single Escape
 * closing the dialog AND popping the app's own route history.
 *
 * Instead of acting immediately, this now just marks the entry
 * `closing` and queues it — `pushOrReplaceModal` (the open-side
 * counterpart, called synchronously moments later in the SAME commit if
 * a sibling is opening) checks this queue FIRST and, when it finds a
 * same-tick match, collapses the pair into one synchronous
 * `history.replaceState` instead: the outgoing entry's own history slot
 * is simply relabeled to belong to the incoming modal, atomically, with
 * no `popstate` to race. Only entries that reach the end of the tick
 * with no such pairing (the common case — a lone modal closing on its
 * own) fall through to the original, real `history.back()` path, via
 * `scheduleReconcileFlush`'s queued microtask.
 */
function reconcileUnclosedEntry(entry: StackEntry): void {
  if (entry.closing) return;
  entry.closing = true;
  pendingReconciles.push(entry);
  scheduleReconcileFlush();
}

/**
 * TD-012 (SA plan §3 clause 2) — the open-side counterpart to
 * `reconcileUnclosedEntry` above (Sprint-011 G1 fix). If a sibling
 * modal's close is still pending reconciliation from THIS SAME
 * synchronous commit (cleanups always run before new effects within one
 * commit, so it's already queued by the time this runs), pair with it:
 * one `history.replaceState` relabels that slot for this modal instead
 * of a `pushState`, keeping real history depth net-zero and side-
 * stepping the `back()`-vs-`pushState` race entirely (nothing async is
 * ever in flight to race). Otherwise (the common case — nothing else is
 * closing this tick), push a fresh ephemeral entry exactly as before.
 */
function pushOrReplaceModal(entry: StackEntry): void {
  const pairedClose = pendingReconciles.shift();
  if (pairedClose) {
    history.replaceState({ hayloftOverlay: true }, "");
  } else {
    history.pushState({ hayloftOverlay: true }, "");
  }
  pushModal(entry);
}

export interface UseModalA11yOptions {
  /** Whether the modal/overlay/sheet is currently open/mounted-and-visible.
   * Effects only run their open-transition work (save-focus, move-focus,
   * install trap) while this is true. */
  active: boolean;
  /** Called on Escape — same semantics as a Cancel/Close affordance. */
  onClose: () => void;
  /** Focus this element first if given; otherwise the first focusable
   * descendant of the container is used. */
  initialFocusRef?: { current: HTMLElement | null };
  /** Sprint-017 E2 (TD-039) — OPTIONAL dirty-state guard. Omit it (the
   * default) and every close path behaves exactly as before. Provide it
   * and the shared close path (Escape + the ×/Cancel/Back button +
   * hardware/browser Back — all of which converge on the one `popstate`
   * handler) awaits it before actually closing: return `false` to ABORT
   * the close (e.g. a "Discard changes?" prompt was declined or is being
   * shown) — the overlay stays open and its history entry is restored;
   * return `true` (or a promise resolving true) to close normally. See the
   * `StackEntry.guard` docstring for the full contract. */
  guard?: () => boolean | Promise<boolean>;
}

export interface UseModalA11yResult {
  /**
   * TD-012 (SA plan §3 clause 3) — wire this to every user-facing close
   * affordance (the ×/Cancel button, backdrop click) INSTEAD OF calling
   * the `onClose`/`onCancel` prop directly. It only ever asks the browser
   * to consume this modal's ephemeral history entry (`history.back()`);
   * the shared `popstate` handler above is the sole caller of the real
   * `onClose`, so a UI dismiss and a hardware/browser Back always
   * converge on that one code path. A no-op once the entry is already
   * closing (double-click / race guard).
   */
  close: () => void;
}

/**
 * Sprint-006 D3 — shared accessibility behavior for every dialog/bottom-
 * sheet/full-screen overlay in the app (design-spec-today-card.md §5 +
 * the sprint-006 D3 task brief's "ALL overlays/sheets ... trap focus,
 * restore focus to trigger on close, close on Escape"). Three things, one
 * hook, applied identically everywhere instead of each component growing
 * its own slightly-different keydown listener:
 *
 * 1. **Escape closes** — routed only to the topmost open modal (see the
 *    stack above), so closing a nested sheet never also closes the
 *    overlay underneath it.
 * 2. **Focus trap** — Tab/Shift+Tab cycle only through focusable elements
 *    inside `containerRef`; focus can never land on anything behind the
 *    overlay while it's open.
 * 3. **Focus restore** — the element that had focus right before the
 *    overlay opened (almost always the button that triggered it) gets
 *    focus back the moment the overlay closes/unmounts, so a keyboard user
 *    never loses their place.
 *
 * Sprint-011 C1 (TD-012, SA plan §3) — a fourth thing, additive to the
 * above, same `active`/`onClose` contract (no call-site signature
 * change): **hardware/browser Back dismisses the topmost overlay.** On
 * every `active` false->true transition this pushes exactly one ephemeral
 * `history.pushState({ hayloftOverlay: true })` entry and registers it on
 * the SAME shared stack the keydown listener already walks, so nesting
 * (overlay -> sheet -> lightbox) peels back one layer per Back, in the
 * same order Escape already does. Callers get the new `{ close }` return
 * value to wire into their own ×/Cancel/backdrop affordances so every
 * close path — Escape, a UI dismiss, or a hardware Back — converges on
 * the shared `popstate` handler as the one place that actually calls
 * `onClose` (clause 3's "single source of truth for close"). A component
 * that closes itself some OTHER way (e.g. a successful form submit that
 * flips its own `active` prop directly) is still covered: the effect
 * cleanup below reconciles any not-yet-consumed entry on unmount so it
 * never becomes a phantom history entry (clause 4).
 */
export function useModalA11y(
  containerRef: { current: HTMLElement | null },
  { active, onClose, initialFocusRef, guard }: UseModalA11yOptions,
): UseModalA11yResult {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Sprint-017 E2 (TD-039) — keep a live ref to the latest `guard` so the
  // stack entry (created once per open transition, deps `[active]`) always
  // invokes the current closure and reads live dirty state, mirroring the
  // `onCloseRef` pattern above. Whether the entry carries a guard AT ALL is
  // fixed at open time from `guard != null`, so an overlay that never opts
  // in keeps `entry.guard === undefined` and the default sync close path.
  const guardRef = useRef(guard);
  guardRef.current = guard;

  const entryRef = useRef<StackEntry | null>(null);

  // Stable identity across renders (the ref it closes over never changes)
  // — safe to hand straight to JSX `onClick` props without re-creating a
  // new function every render.
  const closeRef = useRef((): void => {
    const entry = entryRef.current;
    if (!entry || entry.closing) return;
    // Deliberately does NOT set `entry.closing` here — the shared
    // `popstate` handler is the sole caller of the real `onClose` (SA
    // plan §3 clause 3). This only asks the browser to consume the
    // ephemeral entry; `popstate` does the rest.
    history.back();
  });

  useEffect(() => {
    if (!active) return undefined;

    const previouslyFocused = document.activeElement as HTMLElement | null;

    const container = containerRef.current;
    const toFocus =
      initialFocusRef?.current ?? container?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
    toFocus?.focus();

    function getFocusable(): HTMLElement[] {
      const node = containerRef.current;
      if (!node) return [];
      return Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
    }

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.stopPropagation();
        // TD-012 — Escape is a close affordance too; route it through the
        // same `history.back()` path as the ×/Cancel button and backdrop
        // click (SA plan §3 clause 3) rather than calling `onClose`
        // directly, so it converges on the shared `popstate` handler.
        closeRef.current();
        return;
      }
      if (e.key !== "Tab") return;

      const focusable = getFocusable();
      if (focusable.length === 0) {
        e.preventDefault();
        return;
      }
      const first = focusable[0] as HTMLElement;
      const last = focusable[focusable.length - 1] as HTMLElement;
      const current = document.activeElement;
      const containerNode = containerRef.current;
      const currentIsInside = containerNode ? containerNode.contains(current) : false;

      if (e.shiftKey) {
        if (!currentIsInside || current === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (!currentIsInside || current === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    const entry: StackEntry = {
      onKeyDown,
      onClose: () => onCloseRef.current(),
      // Sprint-017 E2 (TD-039) — carry a guard ONLY if the consumer opted
      // in at open time; delegate through `guardRef` so the latest closure
      // (live dirty state) runs. Absent guard => `undefined` => the default
      // synchronous close path in the `popstate` handler above.
      guard: guardRef.current ? () => guardRef.current?.() ?? true : undefined,
      closing: false,
    };
    entryRef.current = entry;

    // TD-012 (SA plan §3 clause 2) — one ephemeral history entry per open
    // modal. No third `url` argument, so the visible URL never changes —
    // overlays stay un-routed, exactly as ADR-002's original "overlays
    // are local-state-driven, not routed" ruling requires (see the ADR-002
    // A7 amendment this ships alongside). Sprint-011 G1 fix — routed
    // through `pushOrReplaceModal` rather than a bare `pushState`, so a
    // sibling closing in this SAME commit (see `reconcileUnclosedEntry`)
    // pairs into one synchronous `replaceState` instead of racing an
    // async `back()`.
    pushOrReplaceModal(entry);

    return () => {
      removeModal(entry);
      entryRef.current = null;
      // TD-012 (SA plan §3 clause 4) — burn this entry's ephemeral
      // history push now if nothing already did (Escape/Back/UI-dismiss
      // all mark `closing` before this runs); otherwise it's a phantom
      // entry the next real Back would have to consume before doing
      // anything visible.
      reconcileUnclosedEntry(entry);
      previouslyFocused?.focus?.();
    };
  }, [active]);

  return { close: closeRef.current };
}
